import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type React from "react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getErrorDetail(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }
  const response = error.response;
  if (typeof response !== "object" || response === null || !("data" in response)) {
    return undefined;
  }
  const data = response.data;
  if (typeof data !== "object" || data === null || !("detail" in data)) {
    return undefined;
  }
  if (typeof data.detail === "string") return data.detail;
  // Odpowiedzi z kodem (np. 402 `addon_required`, 403 uprawnień) mają opis w `message`.
  const detail = data.detail;
  if (typeof detail === "object" && detail !== null && "message" in detail) {
    return typeof detail.message === "string" ? detail.message : undefined;
  }
  return undefined;
}

/** 402 pokazuje już globalny komunikat z odsyłaczem do zmiany planu. */
export function isPaymentRequired(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return false;
  }
  const response = error.response;
  return (
    typeof response === "object" &&
    response !== null &&
    "status" in response &&
    response.status === 402
  );
}

export function formatCurrency(amount: number, currency: string = "PLN"): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency,
  }).format(amount);
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat("pl-PL").format(num);
}

/**
 * Wyciąga string-label z wartości która może być stringiem, liczbą lub
 * obiektem `{translation: {pl: "..."} | string, type: "..."}` (jak Allegro
 * zwraca część pól wielojęzycznych). Renderowanie obiektu w JSX crashuje
 * React, więc używamy tego wszędzie gdzie label pochodzi z Allegro API.
 */
export function lbl(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    const obj = v as Record<string, unknown>;
    const t = obj.translation;
    if (typeof t === "string") return t;
    if (t && typeof t === "object") {
      const tObj = t as Record<string, unknown>;
      const pl = tObj.pl;
      if (typeof pl === "string") return pl;
      const first = Object.values(tObj).find((x) => typeof x === "string");
      if (typeof first === "string") return first;
    }
    if (typeof obj.value === "string") return obj.value;
    if (typeof obj.name === "string") return obj.name;
    if (typeof obj.label === "string") return obj.label;
    if (typeof obj.text === "string") return obj.text;
    // Unknown shape — fallback do JSON żeby uniknąć Reactowego crashu
    try {
      return JSON.stringify(v);
    } catch {
      return "";
    }
  }
  return "";
}

/**
 * Bezpieczne rzutowanie wartości na ReactNode — chroni przed crashem
 * "Objects are not valid as a React child". Obiekty → lbl(), reszta passthrough.
 */
export function safeNode(v: unknown): React.ReactNode {
  if (v == null) return null;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return v as React.ReactNode;
  }
  if (typeof v === "object") return lbl(v);
  return null;
}

export function formatDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("pl-PL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

export function getRelativeTime(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "przed chwilą";
  if (diffMins < 60) return `${diffMins} min temu`;
  if (diffHours < 24) return `${diffHours} godz. temu`;
  if (diffDays < 7) return `${diffDays} dni temu`;
  return formatDate(d);
}

export function getScoreColor(score: number): string {
  if (score >= 75) return "text-emerald-600 dark:text-emerald-400";
  if (score >= 50) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

export function getScoreBgColor(score: number): string {
  if (score >= 75) return "bg-emerald-500";
  if (score >= 50) return "bg-amber-500";
  return "bg-red-500";
}
